import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { apiFetch } from '../lib/api';

const CONFIRM_WORD = 'STOP';
// Server limit (killSwitchEngageSchema).
const REASON_MAX = 500;

export function KillSwitchDialog({ compact = false }: { compact?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiFetch('/kill-switch/engage', { method: 'POST', body: { reason } }),
    onSuccess: () => {
      setOpen(false);
      setReason('');
      setConfirmText('');
      void queryClient.invalidateQueries({ queryKey: ['health-summary'] });
    },
  });

  const canConfirm =
    confirmText.trim().toUpperCase() === CONFIRM_WORD &&
    reason.trim().length > 0 &&
    reason.length <= REASON_MAX;
  // Typed something, and it isn't the confirm word: the input is invalid
  // and its hint (linked via aria-describedby) says why.
  const confirmMismatch =
    confirmText.length > 0 && confirmText.trim().toUpperCase() !== CONFIRM_WORD;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) mutation.reset();
      }}
    >
      <DialogTrigger asChild>
        {compact ? (
          <button
            type="button"
            className="rounded-md px-1.5 py-1 -mx-1.5 text-[13px] font-semibold text-danger-text hover:bg-danger-bg"
          >
            Stop all sending
          </button>
        ) : (
          <Button variant="danger" className="w-full h-12 font-semibold">
            <AlertTriangle size={16} aria-hidden="true" />
            Kill switch
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        title="Kill switch: stop all sending"
        description="Stops all outgoing mail immediately, across every campaign."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) mutation.mutate();
          }}
        >
          {mutation.isError ? (
            <Alert>The kill switch could not be engaged. Try again.</Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kill-reason">Reason</Label>
            <Input
              id="kill-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={REASON_MAX}
              aria-describedby="kill-reason-count"
              required
            />
            <div id="kill-reason-count" className="text-xs text-muted text-right">
              {reason.length}/{REASON_MAX}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kill-confirm">Type {CONFIRM_WORD} to confirm</Label>
            <Input
              id="kill-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              aria-describedby="kill-confirm-hint"
              aria-invalid={confirmMismatch}
            />
            <div
              id="kill-confirm-hint"
              className="min-h-[18px] text-xs text-danger-text"
              aria-live="polite"
            >
              {confirmMismatch ? `Type exactly "${CONFIRM_WORD}"` : ''}
            </div>
          </div>
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary" className="h-11">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="danger"
              disabled={!canConfirm || mutation.isPending}
              className="h-11"
            >
              {mutation.isPending ? 'Stopping…' : 'Engage kill switch'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
